import { useCallback, useEffect, useState, useRef } from 'react';
import '../../assets/scss/common.scss'; // variables, mixins, etc. are imported here
import '../../assets/reset.css';
import { sendEvent } from '@/api/nativeBridge.ts';
import { useSearchQuery } from '@/queries/hooks/useApiQuery.ts';
import { logger } from '@/utils/logger.ts';
import { Call, CallList } from './types.ts';
import SearchNodata from './components/SearchNodata.tsx';
import { useNavigate } from 'react-router-dom';
import { useTokenStore, useTheme } from '@/hooks/useStore.ts';
import DiscoverTitle from './components/DiscoverTitle.tsx';
import DiscoverCallLog from './components/DiscoverCallLog.tsx';
import DiscoverSwiper from './components/DiscoverSwiper.tsx';

// 슬라이드 추가
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs } from 'swiper/modules';
import SwiperCore from 'swiper';
import SearchLoading from './components/SearchLoading.tsx';

type PropsType = {
  isPack?: boolean;
};

function SearchB({ isPack = false }: PropsType) {
  const NEED_ADJUST_MINIMUM_PX = 636;
  const [searchList, setSearchList] = useState<CallList>({
    callList: [],
  });
  const token = useTokenStore.getState().token;
  const theme = useTheme();
  const navigate = useNavigate();
  const previousErrorState = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { data, isLoading, isSuccess, isError, error, refetch } =
    useSearchQuery();

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  const contentsSwiperRef = useRef<SwiperCore | null>(null);

  // -----------------------------
  //  콜백 정의
  // -----------------------------
  const handleGetNameCallback = useCallback((event: CustomEvent) => {
    const names = event.detail.names;

    logger.debug('names : ', names);
    setSearchList((prevList) => ({
      ...prevList,
      callList: prevList.callList.map((call) => ({
        ...call,
        name:
          names[call.callId]?.toLowerCase() === 'unknown'
            ? '알 수 없음'
            : names[call.callId] || '알 수 없음',
        isValidCall:
          names[call.callId]?.toLowerCase() !== 'unknown' &&
          names[call.callId]?.length > 0
            ? true
            : false,
      })),
    }));
  }, []);

  const handleRefreshViewCallback = useCallback(
    (event: CustomEvent) => {
      const isAgreed = event.detail.isAgreed;
      if (isAgreed) {
        // 만약 응답 데이터에 변경이 있다면 refetch 이후 실행될
        // useEffect 에서 contentsSwiperRef 릍통해 slideTo(0,0) 으로 이동시킨다.
        refetch();
      } else {
        navigate('/');
      }
    },

    [navigate, refetch],
  );

  useEffect(() => {
    window.addEventListener(
      'GET_NAME_CALLBACK',
      handleGetNameCallback as EventListener,
    );
    window.addEventListener(
      'REFRESH_VIEW_CALLBACK',
      handleRefreshViewCallback as EventListener,
    );
    return () => {
      console.log('remove event listeners');
      window.removeEventListener(
        'GET_NAME_CALLBACK',
        handleGetNameCallback as EventListener,
      );
      window.removeEventListener(
        'REFRESH_VIEW_CALLBACK',
        handleRefreshViewCallback as EventListener,
      );
    };
  }, [handleGetNameCallback, handleRefreshViewCallback]);

  // ============ api 데이터 처리 ================
  // 최초, data 들어 왔을때
  useEffect(() => {
    //undefined 였다가 값이 없으면 null 이 됩니다.
    if (data === undefined) return;

    if (data?.callList?.length > 0) {
      // TODO: callId 배열로 GET_NAME 하고 데이터 받음
      sendEvent('GET_NAME', {
        callIds: data.callList.map((call: Call) => call.callId),
      });
      setSearchList(data);
      sendEvent('ON_LOADING_FINISH', {
        count: data.callList.length,
      });
      // 최초 렌더링 이후 다른 탭 갔다가 돌아오거나, back/fore ground 전환시 refech 로 인해 데이터가 추가 된경우
      // tanstack-query 에 의해 refetch on focus 이벤트에 의해 API 호출 하고 나서 추가된 데이터가 있는 경우
      contentsSwiperRef?.current?.slideTo(0, 0);
    }
  }, [data]);

  if (isLoading || data === undefined || token === undefined) {
    return <SearchLoading />;
  }

  if (isSuccess && data?.callList?.length == 0)
    return <SearchNodata></SearchNodata>;

  if (isError) {
    logger.error(error);
    alert(
      '일시적인 오류로 디스커버 페이지가 표시되지 않습니다. 잠시 후 다시 확인 부탁 드립니다.',
    );
    return <SearchNodata></SearchNodata>;
  }

  function calcSlideOffsetAfter(): Promise<number> {
    return new Promise((resolve, reject) => {
      let counter = 0;
      const intervalTimer = setInterval(() => {
        if (counter > 9) {
          clearInterval(intervalTimer);
          reject(0);
        }
        const titleWrapEl = document.querySelector('.title-wrap');
        const contentsWrapEl = document.querySelector(
          '.contents-wrap .swiper-slide',
        );
        if (titleWrapEl && contentsWrapEl) {
          const titleWrapHeight = (
            getComputedStyle(titleWrapEl).height || '0'
          ).replace('px', '');
          const contentsWrapHeight = (
            getComputedStyle(contentsWrapEl).height || '0'
          ).replace('px', '');
          const titleAndContentHeight =
            Number(titleWrapHeight) + Number(contentsWrapHeight);
          const needOffset = window.innerHeight - titleAndContentHeight;
          clearInterval(intervalTimer);
          resolve(needOffset);
        }
        counter = counter + 1;
      }, 180);
    });
  }

  async function updateSlidesOffsetAfter(swiper: SwiperCore) {
    try {
      const offset = await calcSlideOffsetAfter();
      swiper.params.slidesOffsetAfter = offset;
    } catch (e) {
      console.log(e);
      swiper.params.slidesOffsetAfter = 200;
    } finally {
      swiper.update();
    }
  }

  // swiper scrollTop
  const contentsSlideChange = (swiper: SwiperCore) => {
    setActiveIndex(swiper.activeIndex);

    const activeSlide = swiper.slides[swiper.activeIndex] as HTMLDivElement; // 슬라이드 active 지정
    const activeContentsItem = activeSlide.querySelector(
      '.contents-wrap__item',
    ) as HTMLDivElement; // class="contents-wrap__item" 지정

    if (activeSlide.clientHeight < activeContentsItem.clientHeight) {
      // 슬라이드 item높이가 내부 div보다 작을때..
      activeSlide.scrollTop = 0; // slide 내부 컨텐츠 scrollTop
    }
  };

  return (
    <div className={`ixi-O-discover ${theme}`}>
      <DiscoverTitle
        isPack={isPack}
        titleTextList={searchList.callList.map((call) => call.pushBody || '')}
        setThumbsSwiper={setThumbsSwiper}
      />
      <div className="contents-wrap">
        <Swiper
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          modules={[Thumbs]}
          //viewport 에 하나 이상의 아이템이 들어갈때만 조건 추가하도록
          direction={'vertical'}
          slidesPerView={'auto'}
          autoHeight={true}
          observer={true}
          observeParents={true}
          speed={600}
          nested={true}
          onSwiper={(swiper) => {
            contentsSwiperRef.current = swiper;
            updateSlidesOffsetAfter(swiper);
          }}
          onResize={(swiper) => {
            updateSlidesOffsetAfter(swiper);
          }}
          onSlideChange={contentsSlideChange} // slideChange시 scrollTop
          className="contents-wrap__slide"
        >
          {searchList.callList.map((call, index) => (
            <SwiperSlide key={`contents-wrap-slide-item-${index}`}>
              <div
                className="contents-wrap__item"
                key={`call_list_${index}`}
                data-index={index}
              >
                <DiscoverCallLog callItem={call} mainIndex={activeIndex} />
                <DiscoverSwiper
                  callItem={call}
                  mainIndex={activeIndex}
                  dataIndex={index}
                  isPack={isPack}
                  key={`call_id_${call.callId}_swiper_${index}`}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

export default SearchB;
